import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { translator } from 'mage-i18n';

import { getSelectedCountry } from '@/selectors/data';

import {
  getMerchantAwareTranslator,
  getSelectedPlatform as getMerchantPlatform,
  getMerchantCountry,
  getPricingAnnex,
} from '@/selectors/merchant';

import { getSelectedPlatform } from '@/selectors/platforms';

import { withResponsive } from 'mage-components';
import { ProductPricing } from 'moet-core';
import { RadioBullet, SpacerHorizontal, SpacerVertical, Typography } from '@klarna/bubble-ui';

import { KlarnaLogo } from './KlarnaLogo';
import { KustomLogo } from './KustomLogo';
import { ProductCardDetails } from './ProductCardDetails';
import { TextBoxWithArrow } from './TextBoxWithArrow';

const Card = styled.div`
  box-sizing: border-box;
  box-shadow: ${({ selected }) => (selected ? '0px 9px 25px rgba(0, 0, 0, 0.15)' : 'unset')};
  border: ${({ selected }) => (selected ? '2px solid #267a9c' : '1px solid #F0F1F1')};
  border-radius: 8px;

  width: 100%;
  min-width: 260px;
  max-width: 350px;

  position: relative;
  /* so that disabledReason text box does not overlap with the above component */
  margin-top: ${({ disabled, mobile }) => (disabled && mobile ? '40px' : 'unset')};
`;

const Container = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;

  cursor: pointer;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};

  background: ${({ disabled }) => (disabled ? '#f0f1f1' : 'unset')};
  opacity: ${({ disabled }) => (disabled ? '0.5' : 'unset')};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const ProductExplanationContainer = styled.div`
  height: 54px;
`;

const DisabledReasonContainer = styled.div`
  background: unset;
  opacity: unset;
  pointer-events: all;
  cursor: auto;

  position: absolute;
  top: -55px;
  left: 0;
  right: 0;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ProductCard = ({
  extraAttributesToProductFn,
  isMobile,
  isSelectedFn = () => null,
  pricingAnnex,
  product,
  tMerchantAware,
  onChange = () => null,
  platform,
  countryKey,
  t,
}) => {
  const tPrefix = `products-fe.products.${product.id}`;
  const disableClick = e => {
    e.stopPropagation();
    return false;
  };

  const pricing = product.prices?.find(price => price.consumerCountry === countryKey);
  const selected = isSelectedFn(product.id);
  const isCompatible = product.isCompatible && product.isCountryCompatible;
  const isKustomProduct = productId =>
    productId.startsWith('kco') || productId.startsWith('kco_one');

  return (
    <Card disabled={!isCompatible} mobile={isMobile} selected={selected}>
      {!isCompatible && (
        <DisabledReasonContainer onClick={disableClick}>
          <TextBoxWithArrow
            text={t(
              `products-fe.products.not_available${
                product.isCountryCompatible ? '_platform' : '_country'
              }`,
              {
                platform: product.isCountryCompatible ? platform : undefined,
              },
            )}
          />
        </DisabledReasonContainer>
      )}

      <Container
        disabled={!isCompatible}
        onClick={() => product.isCompatible && onChange(product.id)}
        {...extraAttributesToProductFn(product)}
      >
        <Row>
          <TitleContainer>
            {isKustomProduct(product.id) ? <KustomLogo /> : <KlarnaLogo type="pink" />}
            <SpacerHorizontal spaceToken="space/100" />
            <Typography textToken="text-style/headings/blocks/bold/grande">
              {tMerchantAware(`${tPrefix}.name`)}
            </Typography>
          </TitleContainer>

          <div style={{ marginTop: 1, marginRight: 3 }}>
            <RadioBullet checked={selected} />
          </div>
        </Row>

        <SpacerVertical spaceToken="space/150" />

        <Row>
          <ProductExplanationContainer>
            <Typography
              textToken="text-style/text/paragraphs/default/regular"
              colorToken="colors/text/neutral"
            >
              {tMerchantAware(`${tPrefix}.description`)}
            </Typography>
          </ProductExplanationContainer>
        </Row>

        <SpacerVertical spaceToken="space/150" />

        <Row>
          {pricing ? (
            <ProductPricing price={pricing} pricingAnnex={pricingAnnex?.[product.id]} small />
          ) : (
            <SpacerVertical spaceToken="space/150" />
          )}
        </Row>

        <SpacerVertical spaceToken="space/150" />

        <Row>
          <ProductCardDetails disabled={!product.isCompatible} product={product} />
        </Row>
      </Container>
    </Card>
  );
};

ProductCard.propTypes = {
  countryKey: PropTypes.string.isRequired,
  extraAttributesToProductFn: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isSelectedFn: PropTypes.func.isRequired,
  platform: PropTypes.string.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isCompatible: PropTypes.bool.isRequired,
    isCountryCompatible: PropTypes.bool.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        prices: PropTypes.arrayOf(
          PropTypes.shape({
            transactionFee: PropTypes.number.isRequired,
            transactionPercentageFee: PropTypes.number.isRequired,
          }),
        ),
      }),
    ).isRequired,
    pricing: PropTypes.string.isRequired,
    prices: PropTypes.arrayOf(
      PropTypes.shape({
        transactionFee: PropTypes.number.isRequired,
        transactionPercentageFee: PropTypes.number.isRequired,
      }),
    ),
  }).isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  pricingAnnex: PropTypes.objectOf(PropTypes.string),
};

export default compose(
  withResponsive,
  connect(state => ({
    tMerchantAware: getMerchantAwareTranslator(state),
    t: translator(state),
    countryKey: getSelectedCountry(state)?.key || getMerchantCountry(state)?.key,
    platform: getSelectedPlatform(state)?.name || getMerchantPlatform(state)?.name,
    pricingAnnex: getPricingAnnex(state),
  })),
)(ProductCard);
